import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Button,
  Box,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useApp } from '../../context/AppContext.tsx';

export const AboutModal: React.FC = () => {
  const { isAboutModalOpen, setIsAboutModalOpen, setIsPatchNotesOpen, setIsTourOpen } = useApp();

  return (
    <Dialog
      open={isAboutModalOpen}
      onClose={() => setIsAboutModalOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            About Genetics Lab
          </Typography>
          <Chip
            size="small"
            label={`v${__APP_VERSION__}`}
            color="primary"
            variant="outlined"
            sx={{ height: 22, fontWeight: 700, fontFamily: 'monospace', fontSize: '0.75rem' }}
          />
        </Box>
        <IconButton aria-label="Close about dialog" size="small" onClick={() => setIsAboutModalOpen(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
          <Box
            component="img"
            src="./img/items/green-berry.webp"
            alt="Genetics Lab"
            sx={{ width: 64, height: 64, objectFit: 'contain' }}
          />

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Genetics Lab
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
              Rust Plant Genetics &amp; Crossbreeding
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
              <Chip
                size="small"
                label={`v${__APP_VERSION__}`}
                sx={{ height: 20, fontWeight: 800, fontFamily: 'monospace', fontSize: '0.7rem' }}
              />
              <Chip
                size="small"
                label="Stable"
                color="success"
                variant="outlined"
                sx={{ height: 20, fontWeight: 800, fontSize: '0.7rem' }}
              />
            </Box>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, textAlign: 'left' }}>
            Genetics Lab is a fast, client-side plant genetics calculator and farming companion for Rust. It performs exhaustive multi-generation crossbreeding simulations in Web Workers, scores resulting plant clones, and features offline OCR screen-scanning directly from your game window.
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              width: '100%',
              backgroundColor: 'rgba(96, 205, 255, 0.04)',
              borderRadius: 1.5,
              textAlign: 'left'
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', display: 'block', mb: 0.5 }}>
              Architecture
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Built with TypeScript, React, Material UI (MUI v6), Web Workers, and local Tesseract.js.
            </Typography>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          component="a"
          href="https://github.com/JawadYzbk/rust-genetics-lab"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<GitHubIcon />}
          sx={{ borderColor: 'var(--gl-text-faint)', color: 'var(--gl-text-secondary)', fontSize: '0.8rem' }}
        >
          Contribute
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => {
              setIsAboutModalOpen(false);
              setIsTourOpen(true);
            }}
          >
            Tutorial
          </Button>
          <Button
            onClick={() => {
              setIsAboutModalOpen(false);
              setIsPatchNotesOpen(true);
            }}
          >
            Patch notes
          </Button>
          <Button variant="contained" onClick={() => setIsAboutModalOpen(false)}>
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
